import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Checkbox, Spin, Input, Divider, AutoComplete } from 'antd';
import { Form } from "@formio/react";
import intl from 'react-intl-universal';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import _ from 'lodash-es';

import request from "../../../libs/request";
import Evisit from '../../../libs/Evisit';

export const InputOption = forwardRef((props, ref) => {
    const { searchUrl, onValueChange, formatSearchResult } = props;
    const [value, setValue] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [searchText, setSearchText] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (searchText) {
                setLoading(true);
                fetch(`${searchUrl}?q=${searchText}`, {
                    method: 'GET',
                    headers: {
                        'X-ApiToken': window.user.token,
                        'X-AccountCode': window.CONFIG.Account.code
                    }
                })
                    .then(response => {
                        if (!response.ok) {
                            throw new Error('Network response was not ok');
                        }
                        return response.json();
                    })
                    .then(resp => {
                        if (!resp.data) {
                            return;
                        }
                        setOptions(formatSearchResult ? formatSearchResult(resp.data) : resp.data);
                    })
                    .catch(error => {
                        console.error('Error fetching data:', error);
                    }).finally(() => {
                        setLoading(false);
                    });
            }
        }, 300); // 300ms debounce time

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, searchUrl]);

    useEffect(() => {
        if (onValueChange) {
            onValueChange(value);
        }
    }, [value, onValueChange]);

    const handleOnSelect = (val) => {
        const selectedOpt = options.find(o => o.value === val);
        if (selectedOpt) {
            setValue(selectedOpt);
        }
    }

    useImperativeHandle(ref, () => ({
        getValue: () => value
    }));

    return (
        <>
            {
                searchUrl ? (
                    <AutoComplete
                        ref={inputRef}
                        options={options}
                        notFoundContent={isLoading ? (
                            <div className='text-center'>
                                <Spin size="small" />
                            </div>
                        ): <div className='text-center'>{
                            searchText.length > 2 
                                ? intl.get("no_matches_add_new").d("No matches found, click 'Add' to add new") 
                                : intl.get("enter_min_3").d("Please enter 3 or more characters")
                        }</div>}
                        style={{ width: "100%" }}
                        onSearch={setSearchText}
                        onChange={(val) => {
                            setValue(val)
                        }}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") {
                                onValueChange(e.target.value, true);
                            }
                        }}
                        onSelect={handleOnSelect}
                    />
                ) : (
                    <Input
                        ref={inputRef}
                        onChange={(e) => {
                            setValue(e.target.value);
                        }}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") {
                                onValueChange(e.target.value, true);
                            }
                        }}
                    />
                )
            }
        </>
    )
})

const organizeOptions = (options) => {
    var categorizedOptions = {};
    var results = [];
    for (var i in options) {
        if (!options[i]["category"]) {
            results.push(options[i]);
        } else {
            var category = options[i]["category"];
            if (!categorizedOptions[category]) {
                categorizedOptions[category] = [];
            }
            categorizedOptions[category].push(options[i]);
        }
        
    }
    for (var c in categorizedOptions) {
        results.push({
            value: c,
            categoryTitle: true
        })
        for (var j in categorizedOptions[c]) {
            results.push(categorizedOptions[c][j]);
        }
    }
    return results;
}

export const Options = ({data, options, uniqueKey, template, onChange }) => {

    const extendedOptions = () => {
        if (!options || !options.length) {
            // re-map data as template struct
            // e.g. { name: "No Known Medication Allergies", code: "no_known_medication_allergies", dosespot_result: "JSON string" }
            if (data && data.length) {
                return data.map(d => {
                    return _.mapValues(template, (value, key) => {
                        if (d[key]) {
                            return d[key];
                        }
                        return value;
                    });
                });
            }
            
            return []
        }
        let combinedOptions = [];
        if (data && data.length) {
            combinedOptions = options.concat(_.filter(data.map(d => {
                return { value: d[uniqueKey] };
            }), d => {
                return !_.find(options, {value: d["value"]});
            }))
        } else {
            combinedOptions = options;
        }
        combinedOptions = combinedOptions.filter(option => option != null);
        const uniqueOptions = [...new Map(combinedOptions.map(item => [item.value, item])).values()];
        return uniqueOptions;
    };
    var combined = extendedOptions();
    var organized = organizeOptions(combined);
    const values = (data && data.length) ? data.map(d => d[uniqueKey]): [];

    const renderOptions = () => {
        var result = [];
        function getValueText (val) {
            if (typeof val === "string") {
                return val;
            }
            return val[uniqueKey] || val["value"];
        }
        for (var i in organized) {
            if (organized[i]["categoryTitle"]) {
                result.push(
                    <p style={{margin: "5px 0"}}><b>{ organized[i]["value"] }</b></p>
                )
            } else {
                result.push(
                    <div style={{ width: "100%" }} key={i}>
                        <Checkbox style={{ margin: "0 30px" }} value={getValueText(organized[i])} >
                            {getValueText(organized[i])}
                        </Checkbox>
                        <Divider style={{ margin: "5px 0" }} />
                    </div>
                );
            }
        }
        return result;
    }

    const handleOnChanged = (values) => {
        let newValue = [];
        if (template) {
            for (var i in values) {
                // eslint-disable-next-line no-loop-func
                const found = organized.find(o => o[uniqueKey] === values[i]);
                newValue.push(found);
            }
            newValue = newValue.filter(n => n);
        } else {
            newValue = values.map(v => {
                return {
                    [uniqueKey]: v
                }
            });
        }
        onChange({
            data: newValue
        });
    }

    return (
        <>
            <Checkbox.Group
                style={{ width: "100%" }}
                value={values}
                onChange={handleOnChanged}
            >
                {renderOptions()}
            </Checkbox.Group>
        </>
    )
}

const OptionList = (props) => {
    const [updating, setUpdating] = useState(_.get(props, "editMode"));
    const [data, setData] = useState(_.get(props, "data", {}));
    const [validationError, setValidationError] = useState(null);
    const {options} = props;
    const inputRef = useRef(null);

    useEffect(() => {
        validateData(data);
    }, [props["validate"]]);

    const validateData = (dataToCheck) => {
        if (props["required"]
            && !_.get(dataToCheck, "data_not_reported", false)
            && _.isEmpty(_.get(dataToCheck, "data", []))) {
            setValidationError(intl.get("emr_required", {name: props["friendlyName"]}).d("{name} is required."));
            return false
        }
        setValidationError(null);
        return true;
    }

    const submitData = async (postData) => {
        if (!validateData(postData)) {
            return;
        }
        if (props.apiUrl) {
            await fetch(props.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-ApiToken': window.user.token,
                    'X-AccountCode': window.CONFIG.Account.code
                },
                credentials: 'omit',
                body: JSON.stringify(postData),
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .catch(error => {
                console.error('Error posting data:', error);
            });
        }
    }

    const onValueChange = (newValue) => {
        setData(newValue);
        if (props.onChange) {
            props.onChange({
                [props.code]: newValue
            });
        }

        // keep user behavior consistent, submit data on every change but not delay
        submitData(newValue);
    }

    const renderData = () => {
        if (data["data_not_reported"]) {
            return (
                <p>{props["nothingToDeclareTitle"]}</p>
            );
        }

        var list = [];
        for (var i in data["data"]) {
            list.push(<li key={i}>{data["data"][i][props.uniqueKey]}</li>);
        }
        return (
            <ul className="option-list-component-items">
                {list}
            </ul>
        );
    }

    const handleChangeSelection = (val, forceSubmit) => {
        if (!val) {
            return;
        }
        // if type is string, convert to object
        if (typeof val === "string") {
            val = {
                [props.uniqueKey]: val
            };
        }
        // if has optionTemplate, make object map to template
        if (props.optionTemplate) {
            val = _.mapValues(props.optionTemplate, (value, key) => {
                if (val[key]) {
                    return val[key];
                }
                return;
            })
        }

        if (forceSubmit) {
            let submitData = _.clone(data);
            if (!submitData["data"]) {
                submitData["data"] = [];
            }
            submitData["data"].push(val);
            onValueChange(submitData);
            return;
        }
    }

    const renderFields = () => {
        return (<>
            <Checkbox
                style={{ margin: "6px 0" }}
                defaultChecked={data["data_not_reported"]}
                onChange={(e) => {
                    var newValue = _.clone(data);
                    newValue["data_not_reported"] = e.target.checked;
                    newValue["data"] = [];
                    onValueChange(newValue);
                }}>
                <strong>{props["nothingToDeclareTitle"]}</strong>
            </Checkbox>
            {!data["data_not_reported"] && <>
                <Options 
                    data={data['data']} 
                    options={options} 
                    uniqueKey={props.uniqueKey}
                    template={props.optionTemplate}
                    onChange={onValueChange}
                />
                <br />
                <br />
                <div>
                    <label>
                        <b> {props["inputTitle"]} </b>
                    </label>
                </div>
                <Space.Compact style={{ width: "80%" }}>
                    <InputOption
                        searchUrl={props.searchUrl}
                        onValueChange={handleChangeSelection}
                        ref={inputRef}
                        formatSearchResult={props.formatSearchResult}
                    />
                    <Button
                        type="primary"
                        onClick={() => handleChangeSelection(inputRef.current.getValue(), true)}
                    >
                        {intl.get("add").d("Add")}
                    </Button>
                </Space.Compact>
            </>}
        </>);
    }

    return (
        <div className="emr-item">
            { !_.get(props, "editMode") && <>
                <b>
                    {props["friendlyName"]}
                </b>
                { props["required"] && <span className="required-sign">*</span> }
                <Button className="emr-item-update" size="small" onClick={() => { setUpdating(!updating) }}>
                    {updating ? intl.get("cancel").d("Cancel") : intl.get("update").d("Update")}
                </Button>
            </>}
            { validationError &&
                <p className="validation-error">{ validationError }</p>
            }
            <div>
                {updating ? renderFields() : renderData()}
            </div>
        </div>
    );
}

const Pmhx = (props) => {
    return (
        <OptionList
            { ...props }
            code="conditions"
            inputTitle={intl.get("other_cond").d("Other conditions")}
            friendlyName={intl.get("pmhx_title").d("Past and Current Medical Conditions")}
            nothingToDeclareTitle={intl.get("no_pmhx").d("No Past Medical History")}
            uniqueKey="condition"
            apiUrl={`${window.base_url}/api_v3/users/${props["userId"]}/emr/conditions`}
        />
    );
}

const Pshx = (props) => {
    return (
        <OptionList
            { ...props }
            code="procedure"
            inputTitle={  _.get(window.CONFIG, "Account.emr.pshx.placeholder", intl.get("pshx_name").d("Name of Procedure")) }
            friendlyName={ _.get(window.CONFIG, "Account.emr.pshx.friendly_name", intl.get("pshx_title").d("Past Surgical History")) }
            nothingToDeclareTitle={ _.get(window.CONFIG, "Account.emr.pshx.nothing_declare_title", intl.get("no_pshx").d("No Past Surgeries")) }
            uniqueKey="procedure"
            apiUrl={`${window.base_url}/api_v3/users/${props["userId"]}/emr/surgeries`}
        />
    );
}

const Allergies = (props) => {
    return (
        <OptionList
            { ...props }
            code="allergies"
            inputTitle={intl.get("drug_name").d("Name of Medication")}
            friendlyName={intl.get("allergies").d("Allergies")}
            nothingToDeclareTitle={intl.get("no_allergies").d("No Known Medication Allergies")}
            uniqueKey="substance"
            apiUrl={`${window.base_url}/api_v3/users/${props["userId"]}/emr/allergies`}
            searchUrl={`${window.base_url}/api_v3/eprescribe/allergy`}
            optionTemplate={{
                code: "",
                substance: "",
                dosespot_result: ""
            }}
            formatSearchResult={(data) => {
                return data.map(d => {
                    const parsedData = JSON.parse(d.id);
                    return {
                        value: d.text,
                        code: parsedData.RxCUI,
                        substance: parsedData.Name,
                        dosespot_result: d.id
                    }
                });
            }}
        />
    );
}

const Medications = (props) => {
    return (
        <OptionList
            { ...props }
            code="medications"
            inputTitle={intl.get("med_name").d("Name of Medication")}
            friendlyName={intl.get("current_med").d("Current Medications")}
            nothingToDeclareTitle={intl.get("no_med").d("No Known Medications")}
            uniqueKey="name"
            apiUrl={`${window.base_url}/api_v3/users/${props["userId"]}/emr/medications`}
            searchUrl={`${window.base_url}/api_v3/eprescribe/medicine`}
            optionTemplate={{
                code: "",
                name: "",
                dosespot_result: ""
            }}
            formatSearchResult={(data) => {
                return data.map(d => {
                    const parsedData = JSON.parse(d.id);
                    return {
                        value: d.text,
                        code: parsedData.syncId,
                        name: parsedData.name,
                        dosespot_result: d.id
                    }
                });
            }}
        />
    );
}

const getFormSchema = (form) => {
    var schema = {};
    if (!form || !form["components"]) {
        return schema;
    }
    for (var i in form["components"]) {
        schema[form["components"][i]["key"]] = {
            label: form["components"][i]["label"]
        };
    }
    return schema;
}

const HealthHabits = (props) => {
    const [updating, setUpdating] = useState(_.get(props, "editMode"));
    const [data, setData] = useState(_.get(props, "data.data", {}));
    const [form, setForm] = useState(null);

    useEffect(() => {
        const loadForm = async () => {
            request.get(`${window.base_url}/ms/forms/${_.get(props, "input.form_id")}`).then(({data}) => {
                setForm(data);
            });
        }
        loadForm();
    }, []);

    const onValueChange = async (value) => {
        if (_.isEqual(value, data)) {
            return;
        }
        const res = await request.post(`${window.base_url}/api_v3/users/${props["userId"]}/emr/health_habits`, {
            data: value
        });
        if (props.onChange) {
            props.onChange({
                health_habits: {
                    data: value
                }
            });
        }
        setData(_.get(res, "data.data", {}));
    }

    const renderFields = () => {
        return <>
            { form ?
                <Form 
                    form={ form }
                    formReady={(formInstance) => {
                        formInstance.submission = {
                            data: data
                        };
                    }}
                    onChange={ (value) => {
                        onValueChange(value.data);
                    }}
                /> :
                <div className="align-center">
                    <Spin size="large" />
                </div>
            }
        </>;
    }

    const renderData = () => {
        if (_.isEmpty(data)) {
            return <></>;
        }
        var schema = getFormSchema(form);
        var results = [];
        for (var i in schema) {
            results.push(
                <p key={i}>{ schema[i]["label"] + ": " + _.get(data, i, "") }</p>
            );
        }
        return results;
    }

    return (<>
        <Space direction="vertical" size="middle" style={{ display: 'flex' }} className="emr-item">
            <div>
                { !_.get(props, "editMode") && <>
                    <b>
                        { intl.get("health_habits_title").d("Social History") }
                    </b>&nbsp;
                    <Button className="emr-item-update" size="small" onClick={() => { setUpdating(!updating) }}>
                        {updating ? intl.get("cancel").d("Cancel") : intl.get("update").d("Update")}
                    </Button>
                </>}
                <div>
                    {updating ? renderFields() : renderData()}
                </div>
            </div>
        </Space>
    </>);
}

const Emr = ({ currentComponent, onNext, onPrevious, data, isMobileWebview, workflow }) => {
    const [loading, setLoading] = useState(false);
    const [emrData, setEmrData] = useState(_.get(data, "emr.value"));
    const [valid, validate] = useState(0);

    const updateUserId = _.get(data, "search_for_patient.value.id",
        _.get(workflow, "data.patient_id", window.user.id));

    const onChange = (d) => {
        setEmrData(_.assign(emrData, d));
    }

    useEffect(() => {
        if (!_.isEmpty(emrData)) {
            return;
        }
        setLoading(true);
        fetch(`${window.base_url}/api_v3/users/${updateUserId}/emr`, {
            method: "GET",
            headers: {
                "X-ApiToken": window.user.token,
                "X-AccountCode": window.CONFIG.Account.code
            },
            credentials: "omit"
        })
        .then(response => response.json())
        .then(resp => {
            setEmrData(resp.data);
        })
        .catch(error => {
            console.error('Error fetching EMR data:', error);
        }).finally(() => {
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateData = (data) => {
        var setting = _.get(currentComponent, "options.emr", {});
        validate(valid + 1);
        for (var key in setting) {
            if (_.get(setting, key + ".required")
                && !_.get(data, key + ".data_not_reported")
                && _.isEmpty(_.get(data, key + ".data", []))) {
                return false;
            }
        }
        return true;
    }

    const onSubmit = (submit) => {
        if (!validateData(submit)) {
            return true;
        }
        Evisit.postMessageBackToParent({
            name: "emr.data",
            data: emrData
        });
        Evisit.executeActions("submit", currentComponent, {data: submit}, data).then(response => {
            onNext(response);
        });
    }

    const renderEmrForm = () => {
        return (
        <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
            {_.get(window.CONFIG, "Account.emr.pmhx.enabled", true) &&
                <Pmhx
                    userId={updateUserId}
                    required={ _.get(currentComponent, "options.emr.conditions.required", false) }
                    options={_.get(currentComponent, "options.emr.conditions.options", _.get(currentComponent, "data.pmhx", []))}
                    onChange={onChange}
                    data={_.get(emrData, "conditions")}
                    validate={ valid }
                    editMode={ false }
                />
            }
            {_.get(window.CONFIG, "Account.emr.pshx.enabled") &&
                <Pshx
                    userId={updateUserId}
                    required={ _.get(currentComponent, "options.emr.surgeries.required", false) }
                    options={_.get(currentComponent, "options.emr.surgeries.options", _.get(currentComponent, "data.pshx", []))}
                    onChange={onChange}
                    data={_.get(emrData, "surgeries")}
                    validate={ valid }
                />
            }
            <Allergies
                userId={updateUserId}
                required={ _.get(currentComponent, "options.emr.allergies.required", false) }
                onChange={onChange}
                data={_.get(emrData, "allergies")}
                validate={ valid }
            />
            <Medications
                userId={updateUserId}
                required={ _.get(currentComponent, "options.emr.medications.required", false) }
                onChange={onChange}
                data={_.get(emrData, "medications")}
                validate={ valid }
            />
            {_.get(currentComponent, "options.emr.health_habits.enabled") &&
                <HealthHabits
                    userId={updateUserId}
                    onChange={onChange}
                    data={_.get(emrData, "health_habits")}
                    validate={ valid }
                    input={ _.get(currentComponent, "options.emr.health_habits.input", {}) }
                />
            }
            <Flex justify={isMobileWebview ? "center" : "space-between"}>
                {(onPrevious != null && !isMobileWebview) ?
                    <Button onClick={onPrevious}>
                        <LeftOutlined style={{ verticalAlign: "middle" }} />
                        {intl.get("back").d("Back")}
                    </Button> : <div></div>
                }
                <Button
                    type="primary"
                    loading={loading}
                    onClick={() => onSubmit(emrData)}
                    shape={isMobileWebview ? "round" : "default"}
                    size={isMobileWebview ? "large" : "default"}
                >
                    {intl.get("continue").d("Continue")}
                    <RightOutlined style={{ verticalAlign: "middle" }} />
                </Button>
            </Flex>
        </Space>);
    }

    const renderEmrEdit = () => {
        var emrKey = _.get(currentComponent, "options.edit_mode.field", _.get(workflow, "data.key"));
        var emrField = <></>;
        if (emrKey === "conditions") {
            emrField = 
                <Pmhx
                    userId={updateUserId}
                    required={ _.get(currentComponent, "options.emr.conditions.required", false) }
                    options={_.get(currentComponent, "options.emr.conditions.options", _.get(currentComponent, "data.pmhx", []))}
                    onChange={onChange}
                    data={_.get(emrData, "conditions")}
                    validate={ valid }
                    editMode={ true }
                />;
        } else if (emrKey === "surgeries") {
            emrField = 
                <Pshx
                    userId={updateUserId}
                    required={ _.get(currentComponent, "options.emr.surgeries.required", false) }
                    options={_.get(currentComponent, "options.emr.surgeries.options", _.get(currentComponent, "data.pshx", []))}
                    onChange={onChange}
                    data={_.get(emrData, "surgeries")}
                    validate={ valid }
                    editMode={ true }
                />;
        } else if (emrKey === "health_habits") {
            emrField = 
                <HealthHabits
                    userId={updateUserId}
                    onChange={onChange}
                    data={_.get(emrData, "health_habits")}
                    validate={ valid }
                    input={ _.get(currentComponent, "options.emr.health_habits.input", {}) }
                    editMode={ true }
                />;
        } else if (emrKey === "allergies") {
            emrField = 
                <Allergies
                    userId={updateUserId}
                    required={ _.get(currentComponent, "options.emr.allergies.required", false) }
                    onChange={onChange}
                    data={_.get(emrData, "allergies")}
                    validate={ valid }
                    editMode={ true }
                />
        } else if (emrKey === "medications") {
            emrField = 
                <Medications
                    userId={updateUserId}
                    required={ _.get(currentComponent, "options.emr.medications.required", false) }
                    onChange={onChange}
                    data={_.get(emrData, "medications")}
                    validate={ valid }
                    editMode={ true }
                />
        }

        return (
            <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                { emrField }
                <Flex justify={isMobileWebview ? "center" : "flex-end"}>
                    <Button
                        type="primary"
                        loading={loading}
                        onClick={() => onSubmit(emrData)}
                        shape={isMobileWebview ? "round" : "default"}
                        size={isMobileWebview ? "large" : "default"}
                    >
                        {intl.get("done").d("Done")}
                        <RightOutlined style={{ verticalAlign: "middle" }} />
                    </Button>
                </Flex>
            </Space>
        );
    }

    return (
        !emrData ?
            <div className="align-center">
                <Spin size="large" />
            </div> : ( _.get(currentComponent, "options.edit_mode.enabled") ?
                renderEmrEdit() : renderEmrForm()
            )
            
    );
};

export default Emr;